import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Check, Copy, ExternalLink,
          Loader2, Plus, Trash2 } from 'lucide-react';
import { adminApi } from '../../api/client';
import { formatCurrency } from '../../lib/currency';
import { extractManualOrderError } from './manualOrderErrors';

const COUNTRY_OPTIONS = [
  { code: 'BE', label: 'België' },
  { code: 'NL', label: 'Nederland' },
  { code: 'FR', label: 'Frankrijk' },
  { code: 'LU', label: 'Luxemburg' },
];

// Stable empty-line factory — id is only used as a React key.
const _newLine = () => ({
  _k: Math.random().toString(36).slice(2),
  productId: '', quantity: 1,
});

function _initialForm() {
  return {
    // Customer
    userId: '',
    guestEmail: '',
    customerFirstName: '',
    customerLastName: '',
    customerPhone: '',
    // B2B
    b2bOrgId: '',
    prescriberId: '',
    // Address
    street: '', houseNumber: '', bus: '',
    postalCode: '', city: '',
    countryCode: 'BE',
    deliveryMethod: 'HOME',
    carrier: 'BPOST',
    // Lines
    lines: [_newLine()],
    // Notes
    notes: '',
    internalNote: '',
  };
}

export default function AdminManualOrderNew() {
  const navigate = useNavigate();
  const [form, setForm] = useState(_initialForm);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    adminApi.listProducts().then(r => {
      setProducts(Array.isArray(r.data) ? r.data : (r.data?.items || []));
    }).catch(() => setProducts([])).finally(() => setProductsLoading(false));
  }, []);

  const productIndex = useMemo(() => {
    const ix = new Map();
    for (const p of products) ix.set(String(p._id || p.id), p);
    return ix;
  }, [products]);

  const onChange = (k) => (e) => {
    const v = e.target.type === 'number' ? Number(e.target.value) : e.target.value;
    setForm(f => ({ ...f, [k]: v }));
  };
  const onLineChange = (idx, k) => (e) => {
    const v = e.target.type === 'number' ? Number(e.target.value) : e.target.value;
    setForm(f => ({ ...f, lines: f.lines.map((ln, i) => i === idx ? { ...ln, [k]: v } : ln) }));
  };
  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, _newLine()] }));
  const removeLine = (idx) => setForm(f => ({
    ...f,
    lines: f.lines.length === 1 ? [_newLine()] : f.lines.filter((_, i) => i !== idx),
  }));

  // Lightweight client-side total preview — server is source of truth.
  const previewTotal = useMemo(() => {
    let total = 0;
    for (const ln of form.lines) {
      const p = productIndex.get(String(ln.productId));
      if (!p) continue;
      const price = Number(p.price) || 0;
      const vat = Number(p.vatRate) || 0;
      const factor = vat > 1 ? 1 + vat / 100 : 1 + vat;
      total += price * factor * (Number(ln.quantity) || 0);
    }
    return total;
  }, [form.lines, productIndex]);

  const canSubmit = () => {
    if (!form.customerFirstName || !form.customerLastName) return false;
    if (!form.userId && !form.guestEmail) return false;
    if (!form.street || !form.postalCode || !form.city) return false;
    if (!form.lines.some(ln => ln.productId && ln.quantity > 0)) return false;
    return !submitting;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit()) return;
    setSubmitting(true); setError(null);
    try {
      const body = {
        ...form,
        countryCode: form.countryCode.toUpperCase(),
        lines: form.lines
          .filter(ln => ln.productId && ln.quantity > 0)
          .map(ln => ({ productId: ln.productId, quantity: Number(ln.quantity) })),
      };
      // Strip the client-only `_k` from any lingering refs.
      delete body.lines._k;
      const r = await adminApi.createManualOrder(body);
      setResult(r.data);
    } catch (err) {
      setError(extractManualOrderError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = async () => {
    if (!result?.checkoutUrl) return;
    try {
      await navigator.clipboard.writeText(result.checkoutUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      window.prompt('Kopieer de betaallink:', result.checkoutUrl);
    }
  };

  // ── Success screen ────────────────────────────────────────────────
  if (result) {
    return (
      <div data-testid="admin-manual-order-success" className="p-8 max-w-3xl mx-auto">
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
          <div className="flex items-start gap-3">
            <Check className="w-6 h-6 text-emerald-700 mt-0.5" />
            <div className="flex-1">
              <h1 className="font-serif text-2xl text-emerald-900">Manuele bestelling aangemaakt</h1>
              <p className="text-sm text-emerald-800 mt-1">
                De bestelling staat in afwachting van betaling. Er is nog <strong>geen ordernummer</strong> en <strong>geen factuur</strong> aangemaakt — die volgen automatisch zodra Mollie de betaling bevestigt.
              </p>
            </div>
          </div>

          <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <div>
              <dt className="text-xs uppercase tracking-wider text-emerald-700">Order ID</dt>
              <dd data-testid="result-order-id" className="font-mono text-emerald-900">{result.orderId}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wider text-emerald-700">Totaal</dt>
              <dd data-testid="result-total" className="font-semibold text-emerald-900">{formatCurrency(result.totalAmount)}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-xs uppercase tracking-wider text-emerald-700">Pending referentie</dt>
              <dd className="font-mono text-xs text-emerald-900 break-all">{result.pendingReference}</dd>
            </div>
          </dl>

          <div className="mt-6 p-4 bg-white rounded-xl border border-emerald-200">
            <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700 mb-2">Mollie betaallink</p>
            <p data-testid="result-checkout-url" className="font-mono text-xs text-foreground break-all mb-3">
              {result.checkoutUrl}
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                data-testid="copy-checkout-url"
                onClick={copyLink}
                className="inline-flex items-center gap-1.5 bg-card border border-border rounded-full px-4 py-2 text-xs font-semibold hover:bg-muted"
              >
                <Copy className="w-3.5 h-3.5" /> {copied ? 'Gekopieerd!' : 'Kopieer link'}
              </button>
              <a
                data-testid="open-checkout-url"
                href={result.checkoutUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground rounded-full px-4 py-2 text-xs font-semibold hover:bg-primary/90"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Open betaallink
              </a>
            </div>
          </div>

          <div className="mt-4 p-3 rounded-xl border border-amber-200 bg-amber-50 text-amber-900 text-xs flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>
              <strong>Geen e-mail verzonden.</strong> Deel de link met de klant via je eigen kanaal (e-mail, WhatsApp, telefoon). Een automatische verzending volgt in een latere slice.
            </span>
          </div>

          <div className="mt-6 flex gap-2">
            <button
              data-testid="result-back-to-orders"
              onClick={() => navigate('/admin/orders')}
              className="px-5 py-2 rounded-full text-sm font-semibold bg-card border border-border hover:bg-muted"
            >
              Terug naar bestellingen
            </button>
            <button
              data-testid="result-new-order"
              onClick={() => { setResult(null); setForm(_initialForm()); }}
              className="px-5 py-2 rounded-full text-sm font-semibold bg-card border border-border hover:bg-muted"
            >
              Nog een manuele bestelling
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Form ──────────────────────────────────────────────────────────
  return (
    <form
      onSubmit={submit}
      data-testid="admin-manual-order-page"
      className="p-8 max-w-4xl mx-auto space-y-8"
    >
      <div>
        <Link to="/admin/orders"
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-2">
          <ArrowLeft className="w-3.5 h-3.5" /> Terug naar bestellingen
        </Link>
        <h1 className="font-serif text-3xl font-light text-foreground">Manuele bestelling</h1>
        <p data-testid="paid-only-notice" className="text-sm text-muted-foreground mt-1 max-w-2xl">
          De bestelling krijgt pas een <strong>ordernummer</strong> en <strong>factuur</strong> na betaling via Mollie. Voorraad wordt pas verlaagd na bevestigde betaling.
        </p>
      </div>

      {error && (
        <div data-testid="form-error"
             className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 whitespace-pre-line">
          {error}
        </div>
      )}

      {/* Customer */}
      <Section title="Klant">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="E-mailadres (gast)" required={!form.userId}>
            <input data-testid="field-guestEmail" type="email" value={form.guestEmail}
                   onChange={onChange('guestEmail')}
                   placeholder="klant@voorbeeld.be"
                   className={inputCls} />
          </Field>
          <Field label="Bestaande userId (optioneel, geavanceerd)">
            <input data-testid="field-userId" type="text" value={form.userId}
                   onChange={onChange('userId')}
                   placeholder="laat leeg voor gastbestelling"
                   className={inputCls} />
          </Field>
          <Field label="Voornaam" required>
            <input data-testid="field-customerFirstName" type="text" required value={form.customerFirstName}
                   onChange={onChange('customerFirstName')} className={inputCls} />
          </Field>
          <Field label="Achternaam" required>
            <input data-testid="field-customerLastName" type="text" required value={form.customerLastName}
                   onChange={onChange('customerLastName')} className={inputCls} />
          </Field>
          <Field label="Telefoon">
            <input data-testid="field-customerPhone" type="tel" value={form.customerPhone}
                   onChange={onChange('customerPhone')} className={inputCls} />
          </Field>
        </div>
      </Section>

      {/* B2B */}
      <Section title="B2B (optioneel)">
        <p className="text-xs text-muted-foreground -mt-2">
          Vul de canonieke <code className="font-mono">b2bOrgId</code> in als deze bestelling op naam van een prescriber/B2B organisatie moet komen. De V6 factuurlogica gebruikt automatisch de canonieke BTW &amp; bedrijfsnaam van de organisatie. Een dedicated picker volgt in Slice C.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="b2bOrgId (advanced)">
            <input data-testid="field-b2bOrgId" type="text" value={form.b2bOrgId}
                   onChange={onChange('b2bOrgId')}
                   placeholder="MongoDB ObjectId"
                   className={inputCls + ' font-mono text-xs'} />
          </Field>
          <Field label="prescriberId (advanced)">
            <input data-testid="field-prescriberId" type="text" value={form.prescriberId}
                   onChange={onChange('prescriberId')}
                   placeholder="MongoDB ObjectId"
                   className={inputCls + ' font-mono text-xs'} />
          </Field>
        </div>
      </Section>

      {/* Address */}
      <Section title="Bezorgadres">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <Field label="Straat" required className="md:col-span-4">
            <input data-testid="field-street" type="text" required value={form.street}
                   onChange={onChange('street')} className={inputCls} />
          </Field>
          <Field label="Nr." className="md:col-span-1">
            <input data-testid="field-houseNumber" type="text" value={form.houseNumber}
                   onChange={onChange('houseNumber')} className={inputCls} />
          </Field>
          <Field label="Bus" className="md:col-span-1">
            <input data-testid="field-bus" type="text" value={form.bus}
                   onChange={onChange('bus')} className={inputCls} />
          </Field>
          <Field label="Postcode" required className="md:col-span-2">
            <input data-testid="field-postalCode" type="text" required value={form.postalCode}
                   onChange={onChange('postalCode')} className={inputCls} />
          </Field>
          <Field label="Gemeente" required className="md:col-span-2">
            <input data-testid="field-city" type="text" required value={form.city}
                   onChange={onChange('city')} className={inputCls} />
          </Field>
          <Field label="Land" required className="md:col-span-2">
            <select data-testid="field-countryCode" value={form.countryCode}
                    onChange={onChange('countryCode')} className={inputCls}>
              {COUNTRY_OPTIONS.map(c => (
                <option key={c.code} value={c.code}>{c.label}</option>
              ))}
            </select>
          </Field>
        </div>
        <p className="text-xs text-muted-foreground -mb-2">
          Verzending uitsluitend naar BE / NL / FR / LU. Levering: bezorging aan huis (BPOST). Afhaalpunten en lockers worden in deze slice niet ondersteund.
        </p>
      </Section>

      {/* Lines */}
      <Section title="Producten">
        <div className="space-y-3">
          {form.lines.map((ln, idx) => {
            const prod = productIndex.get(String(ln.productId));
            return (
              <div key={ln._k}
                   data-testid={`order-line-${idx}`}
                   className="grid grid-cols-12 gap-3 items-end">
                <div className="col-span-12 md:col-span-7">
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">Product</label>
                  <select
                    data-testid={`line-${idx}-product`}
                    value={ln.productId}
                    onChange={onLineChange(idx, 'productId')}
                    className={inputCls}
                    disabled={productsLoading}
                  >
                    <option value="">{productsLoading ? 'Producten laden…' : '— Selecteer product —'}</option>
                    {products.map(p => (
                      <option key={p._id || p.id} value={p._id || p.id}>
                        {p.name_nl || p.name || p.sku || '?'}
                        {p.stock !== undefined ? ` · stock ${p.stock}` : ''}
                        {p.price !== undefined ? ` · ${formatCurrency(p.price)}` : ''}
                      </option>
                    ))}
                  </select>
                  {prod && (
                    <p className="text-xs text-muted-foreground mt-1">
                      VAT {((Number(prod.vatRate) || 0) * (Number(prod.vatRate) > 1 ? 1 : 100)).toFixed(0)}%
                      {prod.stock !== undefined && ` · ${prod.stock} op voorraad`}
                    </p>
                  )}
                </div>
                <div className="col-span-8 md:col-span-3">
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">Aantal</label>
                  <input data-testid={`line-${idx}-quantity`} type="number" min={1} max={999}
                         value={ln.quantity} onChange={onLineChange(idx, 'quantity')}
                         className={inputCls} />
                </div>
                <div className="col-span-4 md:col-span-2 flex gap-2">
                  <button type="button"
                          data-testid={`line-${idx}-remove`}
                          onClick={() => removeLine(idx)}
                          className="flex-1 inline-flex items-center justify-center rounded-full border border-border bg-card px-3 py-2 text-xs text-muted-foreground hover:text-rose-700 hover:border-rose-200">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        <button type="button"
                data-testid="add-line-btn"
                onClick={addLine}
                className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-border px-4 py-2 text-xs font-semibold text-muted-foreground hover:text-foreground hover:border-primary/30">
          <Plus className="w-3.5 h-3.5" /> Regel toevoegen
        </button>
      </Section>

      {/* Notes */}
      <Section title="Notities (optioneel)">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Notitie voor klant (zichtbaar)">
            <textarea data-testid="field-notes" value={form.notes}
                      onChange={onChange('notes')} rows={3} className={inputCls} />
          </Field>
          <Field label="Interne notitie (admin only)">
            <textarea data-testid="field-internalNote" value={form.internalNote}
                      onChange={onChange('internalNote')} rows={3} className={inputCls} />
          </Field>
        </div>
      </Section>

      {/* Footer */}
      <div className="flex items-center justify-between sticky bottom-0 bg-background border-t border-border py-4 -mx-8 px-8">
        <div className="text-sm text-muted-foreground">
          Voorbeeld totaal incl. BTW:&nbsp;
          <span data-testid="preview-total" className="font-semibold text-foreground">
            {formatCurrency(previewTotal)}
          </span>
          <span className="ml-2 text-xs">(server bevestigt definitieve prijs)</span>
        </div>
        <button
          type="submit"
          data-testid="submit-manual-order"
          disabled={!canSubmit()}
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-6 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-all active:scale-95 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {submitting ? 'Aanmaken…' : 'Aanmaken & betaallink genereren'}
        </button>
      </div>
    </form>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────
const inputCls =
  'w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30';

function Section({ title, children }) {
  return (
    <section className="bg-card border border-border rounded-2xl p-6 space-y-4">
      <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{title}</h2>
      {children}
    </section>
  );
}

function Field({ label, required, className = '', children }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-xs font-semibold text-muted-foreground mb-1">
        {label}{required && <span className="text-rose-600">&nbsp;*</span>}
      </span>
      {children}
    </label>
  );
}

/**
 * Friendly extraction of the FastAPI `detail` shape is implemented in
 * `./manualOrderErrors.js` so unit tests can import it without dragging
 * the whole route-coupled component graph into Jest's resolver.
 */
